import type { Bundle, Lesson } from "@otto/lehrgang";
import type { Origin } from "../../content/lib/content.js";
import { originIsFallback, originLabel } from "../../content/lib/originLabel.js";
import type { Progress } from "../../progress/lib/progress.js";
import type { Gate } from "../lib/gate.js";
import { FingerLegend } from "./FingerLegend.js";
import { GUEST_LESSONS, LessonList } from "./LessonList.js";
import { AssessmentCard } from "../../assessment/components/AssessmentCard.js";
import styles from "../../../shared/components/shell.module.css";

/**
 * The screen a child lands on: the six lessons, a note about where progress
 * lives, and the finger legend.
 *
 * The note differs by mode and that difference matters. In guest mode progress
 * sits on the device, so on a shared iPad the next child would see the previous
 * child's stars — hence a way to clear it. With a code there is a "done" button
 * that also wipes the code.
 */
export function LessonOverview({
  bundle,
  origin,
  progress,
  hasCode,
  gate,
  onChoose,
  assessment = null,
  onAssessment = null,
  courseTitle = null,
  onSwitchCourse = null,
  onForgetProgress,
  onFinish,
}: {
  bundle: Bundle;
  origin: Origin;
  progress: Progress;
  hasCode: boolean;
  /** Wie die Klasse ihre Lektionen freigibt. Im Gastmodus immer „frei“. */
  gate: Gate;
  onChoose: (lesson: Lesson) => void;
  /** Der Test, der ansteht – oder null. */
  assessment?: { phase: "vorher" | "nachher"; blocking: boolean } | null;
  onAssessment?: (() => void) | null;
  /** Nur gesetzt, wenn die Klasse mehr als einen Lehrgang führt. */
  courseTitle?: string | null;
  onSwitchCourse?: (() => void) | null;
  onForgetProgress: () => void;
  onFinish: () => void;
}) {
  const hasPractised = Object.keys(progress).length > 0;
  const waiting = assessment?.blocking === true;

  return (
    <div className={styles.column}>
      {/* Nur bei mehreren Lehrgängen. Sonst stünde über der Übersicht der Name
          von etwas, wozu es keine Alternative gibt. */}
      {onSwitchCourse && (
        <p className={styles.footnote}>
          Du übst in <strong>{courseTitle}</strong>.{" "}
          <button type="button" className={styles.textButton} onClick={onSwitchCourse}>
            Anderer Lehrgang
          </button>
        </p>
      )}

      {/* Der Test steht ÜBER den Lektionen, wenn er ansteht: er ist der erste
          Schritt, und ein Kind soll ihn nicht suchen müssen. */}
      {assessment && onAssessment && (
        <AssessmentCard phase={assessment.phase} onStart={onAssessment} />
      )}

      {/* Blass ohne Begründung wäre eine Sackgasse – der Satz steht daneben und
          nicht in einer Meldung, die weggeht. */}
      {waiting && (
        <p className={styles.footnote}>
          Die Lektionen kommen gleich. Mach zuerst den Test – am Ende schauen wir, was du
          gelernt hast.
        </p>
      )}

      <div className={waiting ? styles.waiting : undefined} aria-disabled={waiting}>
        <LessonList
          bundle={bundle}
          progress={progress}
          hasCode={hasCode}
          gate={gate}
          onChoose={waiting ? () => {} : onChoose}
        />
      </div>

      {/* Eine graue Kachel ohne Begründung ist eine Sackgasse. Der Satz sagt,
          warum sie grau ist und was hilft. */}
      {!hasCode && (
        <p className={styles.footnote}>
          Zum Ausprobieren sind die ersten {GUEST_LESSONS} Lektionen offen. Mit einem
          Übungscode von deiner Lehrerin oder deinem Lehrer kannst du alle üben.
        </p>
      )}

      {/* Für die Lehrkraft prüfbar, für das Kind unauffällig: eine Zeile, kein
          Alarm. Nur der Rückfall ist hervorgehoben - er ist der Fall, in dem
          jemand etwas tun muss. */}
      <p className={originIsFallback(origin) ? styles.warnNote : styles.footnote}>
        {originLabel(origin)}
      </p>

      {hasCode ? (
        <p className={styles.footnote}>
          Du übst mit deinem Code.{" "}
          <button type="button" className={styles.textButton} onClick={onFinish}>
            Fertig – für das nächste Kind aufräumen
          </button>
        </p>
      ) : (
        hasPractised && (
          <p className={styles.footnote}>
            Dein Fortschritt bleibt nur auf diesem Gerät.{" "}
            <button
              type="button"
              className={styles.textButton}
              onClick={onForgetProgress}
            >
              Fortschritt löschen
            </button>
          </p>
        )
      )}

      <FingerLegend bundle={bundle} />
    </div>
  );
}
